// ============================================================================
// Stage checklist — the sub-steps a single operation stage is made of, and where
// each one stands. Rendered in the expanded row of the stage table.
//
// The rows come straight from the backend's declared step list for the leg (the
// same list that drives it), so this is a faithful "what's done / what's pending"
// view, not a reconstruction. Each step is one of three kinds under the hood —
// an artifact we create (a Zoho doc / a truck booking), a signal we wait for
// (picked up, delivered), or a check gate (payment cleared, qty received) — but
// the operator only needs the state, so we render them uniformly.
// ============================================================================
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  LoadingOutlined,
  MinusCircleOutlined,
  PauseCircleFilled,
} from "@ant-design/icons";
import { Tag, Tooltip } from "antd";
import React from "react";
import { StageStep, StepState } from "../types";

// Step keys whose humanized form wouldn't say what the step MEANS. `picked_up` sits between
// `dispatched` and the customer docs on a SHIPMENT: dispatched is only the vehicle allotment,
// picked_up is the goods physically leaving — and billing waits on the latter (invoice/shipment
// rows block on picked_up), so its label must read as "the goods have left", not echo Dispatched.
const STEP_LABELS: Record<string, string> = {
  picked_up: "Goods in transit",
};

// Turn a step key into a readable label: "payment_cleared" -> "Payment cleared".
const humanize = (key: string): string => {
  const custom = STEP_LABELS[key];
  if (custom) return custom;
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Per-state presentation: icon + colour + a short word for the state tag. Mirrors the
// stage-level StatusTag palette so the two read as one system (done→green, in-progress→blue,
// failed→red, blocked/held→gold, skipped/pending→muted).
const STATE_META: Record<
  StepState,
  { icon: React.ReactNode; color: string; label: string; muted?: boolean }
> = {
  done: { icon: <CheckCircleFilled />, color: "success", label: "done" },
  in_progress: { icon: <LoadingOutlined />, color: "processing", label: "in progress" },
  failed: { icon: <CloseCircleFilled />, color: "error", label: "failed" },
  blocked: { icon: <PauseCircleFilled />, color: "gold", label: "blocked" },
  pending: { icon: <ClockCircleOutlined />, color: "default", label: "pending", muted: true },
  skipped: {
    icon: <MinusCircleOutlined />,
    color: "default",
    label: "not applicable",
    muted: true,
  },
};

const StepRow: React.FC<{ step: StageStep }> = ({ step }) => {
  const meta = STATE_META[step.state] ?? STATE_META.pending;
  const waiting =
    step.state !== "done" && step.blocking.length > 0
      ? `Waiting on: ${step.blocking.map(humanize).join(", ")}`
      : null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 0",
        opacity: meta.muted ? 0.55 : 1,
      }}
    >
      <span style={{ fontSize: 14, display: "inline-flex", width: 16, justifyContent: "center" }}>
        {meta.icon}
      </span>
      <span style={{ minWidth: 190, fontWeight: 500 }}>
        {humanize(step.key)}
        {!step.required && step.state !== "skipped" ? (
          <Tooltip title="Tracked, but not required for this leg to complete">
            <Tag style={{ marginLeft: 6 }} bordered={false}>
              optional
            </Tag>
          </Tooltip>
        ) : null}
      </span>
      <Tag color={meta.color} bordered={false}>
        {meta.label}
      </Tag>
      {waiting ? <span style={{ color: "#8c8c8c", fontSize: 12 }}>{waiting}</span> : null}
    </div>
  );
};

// The whole checklist for one stage. `blocking` on the first not-done step is usually the real
// holdup, so a glance down the list lands on it.
const StageChecklist: React.FC<{ steps: StageStep[] }> = ({ steps }) => {
  if (!steps || steps.length === 0) {
    return <span style={{ color: "#bbb" }}>No sub-steps for this leg.</span>;
  }
  const done = steps.filter((s) => s.state === "done").length;
  const counted = steps.filter((s) => s.state !== "skipped").length;
  return (
    <div style={{ padding: "4px 8px 8px 32px" }}>
      <div style={{ marginBottom: 6, color: "#595959", fontSize: 12 }}>
        Steps · {done}/{counted} done
      </div>
      {steps.map((step) => (
        <StepRow key={step.key} step={step} />
      ))}
    </div>
  );
};

export default StageChecklist;
